import { useState, useEffect, useCallback, useRef } from 'react';
import { getPlatformOverview, listRegistrationRequests, getHealthStatus } from '../api/superAdminApi';
import type { PlatformOverview, RegistrationRequest, HealthStatus } from '../types';

interface UsePlatformOverviewResult {
  overview: PlatformOverview | null;
  requests: RegistrationRequest[];
  health: HealthStatus | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function usePlatformOverview(): UsePlatformOverviewResult {
  const [overview, setOverview] = useState<PlatformOverview | null>(null);
  const [requests, setRequests] = useState<RegistrationRequest[]>([]);
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  const fetch = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const thisRequestId = ++requestIdRef.current;

    setLoading(true);
    setError(null);

    // Fire all requests in parallel — set data as each completes
    const promises = [
      getPlatformOverview(controller.signal)
        .then((data) => {
          if (thisRequestId === requestIdRef.current) setOverview(data);
        })
        .catch((err) => {
          if (thisRequestId === requestIdRef.current && err?.name !== 'AbortError') {
            setError(err.message || 'Failed to load platform overview');
          }
        }),

      listRegistrationRequests(controller.signal)
        .then((data) => {
          if (thisRequestId === requestIdRef.current) {
            setRequests(
              (data.requests || [])
                .filter((r: RegistrationRequest) => r.status === 'submitted' || r.status === 'invited')
                .slice(0, 10)
            );
          }
        })
        .catch(() => {}), // Non-blocking

      getHealthStatus(controller.signal)
        .then((data) => {
          if (thisRequestId === requestIdRef.current) setHealth(data);
        })
        .catch(() => {}), // Non-blocking
    ];

    await Promise.allSettled(promises);

    if (thisRequestId === requestIdRef.current) {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
    return () => { abortRef.current?.abort(); };
  }, [fetch]);

  return { overview, requests, health, loading, error, refresh: fetch };
}
